import { Component, inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  isFieldValueEditedMessage,
  isUpdateForm,
  isMeasureValueEditedMessage,
  isNotificationMessage,
  isUpdateSubform,
} from '../../guards';
import { Message } from '../../model';
import { LayoutService } from '../../services';
import { RxStompService } from '../../socket';
import { InputFormDataStore } from '../input-form-data/input-form-data.store';
import { UserStore } from '../user/user.store';
import { InputFormStore } from '../input-form-data/input-form.store';
import { TopbarComponent } from './topbar/topbar.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { FooterComponent } from './footer/footer.component';
import { RanneyWellStore } from '../ranney-well/ranney-well.store';
import { CheckPointStore } from '../check-point/check-point.store';
import { MeasuresStore } from '../measures/measures.store';
import { UnitOfMeasuresStore } from '../unit-of-measures/unit-of-measures.store';
import { ValidationRulesStore } from '../validation-rules/validation-rules.store';

@Component({
  selector: 'rb-layout',
  imports: [FooterComponent, TopbarComponent, RouterOutlet, SidebarComponent, NgClass],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  providers: [
    UserStore,
    InputFormDataStore,
    InputFormStore,
    RanneyWellStore,
    CheckPointStore,
    MeasuresStore,
    UnitOfMeasuresStore,
    ValidationRulesStore,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  menuOutsideClickListener!: (() => void) | null;
  @ViewChild(SidebarComponent) appSidebar!: SidebarComponent;
  @ViewChild(TopbarComponent) appTopBar!: TopbarComponent;
  private subscription = new Subscription();

  renderer = inject(Renderer2);
  router = inject(Router);
  readonly layoutService = inject(LayoutService);
  readonly rxStompService = inject(RxStompService);
  readonly inputFormStore = inject(InputFormStore);

  public$: Observable<Message> = this.rxStompService.watch('/public').pipe(map(response => JSON.parse(response.body)));

  ngOnInit(): void {
    this.subscription.add(
      this.layoutService.overlayOpen$.subscribe(() => {
        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen('document', 'click', event => {
            if (this.isOutsideClicked(event)) {
              this.hideMenu();
            }
          });
        }

        if (this.layoutService.appState().staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      })
    );

    this.subscription.add(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        this.hideMenu();
      })
    );

    this.subscription.add(
      this.public$
        .pipe(
          filter(isMeasureValueEditedMessage),
          map(msg => msg.payload)
        )
        .subscribe({
          next: msg => {
            this.inputFormStore.updateCell(msg, 'edit-success');
          },
        })
    );

    this.subscription.add(
      this.public$
        .pipe(
          filter(isFieldValueEditedMessage),
          map(msg => msg.payload)
        )
        .subscribe({
          next: msg => {
            this.inputFormStore.updateField(msg, 'edit-success');
          },
        })
    );

    this.subscription.add(
      this.public$
        .pipe(
          filter(isUpdateForm),
          map(msg => msg.payload)
        )
        .subscribe({
          next: msg => {
            this.inputFormStore.updateForm(msg);
          },
        })
    );

    this.subscription.add(
      this.public$
        .pipe(
          filter(isUpdateSubform),
          map(msg => msg.payload)
        )
        .subscribe({
          next: msg => {
            this.inputFormStore.updateSubform(msg);
          },
        })
    );

    this.subscription.add(
      this.public$
        .pipe(
          filter(isNotificationMessage),
          map(msg => msg.payload)
        )
        .subscribe({
          next: msg => console.log(msg),
        })
    );
  }

  isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    const eventTarget = event.target as Node;

    return !(
      sidebarEl?.isSameNode(eventTarget) ||
      sidebarEl?.contains(eventTarget) ||
      topbarEl?.isSameNode(eventTarget) ||
      topbarEl?.contains(eventTarget)
    );
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.appState.update(prev => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get containerClass() {
    return {
      'layout-static-inactive': this.layoutService.appState().staticMenuDesktopInactive,
      'layout-overlay-active': this.layoutService.appState().overlayMenuActive,
      'layout-mobile-active': this.layoutService.appState().staticMenuMobileActive,
    };
  }
}
